// client/src/pages/School_Admin/Finance/PaymentProcessingPage.js
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Chip,
  Snackbar,
  Alert,
  Typography
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';

import {fetchJson} from '../../../utils/fetchJson';
import { buildReceipt, receiptHtml, canIssueReceipt, feeStatusFor } from '../../../utils/receipt';
import AppLoader from '../../../components/AppLoader';

const PaymentProcessingPage = () => {
  const { t, i18n } = useTranslation();
  // Context-aware cache keys
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  const cacheKeyPayments = `finance:payments:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  const cacheKeyStudents = `finance:students:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  const cacheKeyStructures = `finance:feeStructures:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;
  const cacheKeyClasses = `finance:classes:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;

  const cachedPayments = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyPayments) || '[]'); } catch { return []; } })();
  const cachedStudents = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyStudents) || '[]'); } catch { return []; } })();
  const cachedStructures = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyStructures) || '[]'); } catch { return []; } })();
  const cachedClasses = (() => { try { return JSON.parse(localStorage.getItem(cacheKeyClasses) || '[]'); } catch { return []; } })();

  const [payments, setPayments] = useState(Array.isArray(cachedPayments) ? cachedPayments : []);
  const [students, setStudents] = useState(Array.isArray(cachedStudents) ? cachedStudents : []);
  const [feeStructures, setFeeStructures] = useState(Array.isArray(cachedStructures) ? cachedStructures : []);
  const [classes, setClasses] = useState(Array.isArray(cachedClasses) ? cachedClasses : []);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schoolAccounts, setSchoolAccounts] = useState({ mtnAccountNumber: '', orangeAccountNumber: '' });
  const [schoolName, setSchoolName] = useState('');
  const guardRef = useRef(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    classId: '',
    studentId: '',
    feeStructureId: '',
    amount: '',
    paymentMethod: 'mtn',
    msisdn: '',
    payerEmail: '',
    otp: '',
    reference: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    student: 'all',
    status: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [tab, setTab] = useState('completed');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [formOutstanding, setFormOutstanding] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsPayment, setDetailsPayment] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [methodAvailability, setMethodAvailability] = useState({ offline: true, mtn: false, stripe: false, orange: false });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    // 2s guard if no cache
    if ((payments || []).length === 0) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    fetchData();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, [filters, tab]);

  useEffect(() => {
    const loadSchoolAccounts = async () => {
      try {
        const res = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
        const school = (res && (res.data?.school || res.school || res)) || {};
        setSchoolAccounts({
          mtnAccountNumber: school.mtnAccountNumber || '',
          orangeAccountNumber: school.orangeAccountNumber || ''
        });
        setSchoolName(school.name || school.schoolName || '');
      } catch (_) {
        // non-blocking
      }
    };
    loadSchoolAccounts();
  }, []);

  useEffect(() => {
    const loadAvailability = async () => {
      try {
        const res = await fetchJson('/api/finance/payments/available-methods', { method: 'GET' });
        const data = res?.data || res?.data?.data || res?.data?.methods || res?.methods || res?.data;
        if (data && typeof data === 'object') {
          setMethodAvailability(prev => ({ ...prev, ...data }));
        }
      } catch (_) {
        // non-blocking
      }
    };
    loadAvailability();
  }, []);

  const fetchData = async () => {
    try {
      const statusParam = ['completed', 'pending', 'failed'].includes(String(tab)) ? `?status=${encodeURIComponent(String(tab))}` : '';
      const [paymentsRes, studentsRes, feeStructuresRes, classesRes] = await Promise.all([
        fetchJson(`/api/finance/payments${statusParam}`, { method: 'GET' }),
        fetchJson('/api/students/for-allocations', { method: 'GET' }),
        fetchJson('/api/finance/fee-structures', { method: 'GET' }),
        fetchJson('/api/classes/for-allocations', { method: 'GET' })
      ]);

      const pay = Array.isArray(paymentsRes.data) ? paymentsRes.data : (paymentsRes.payments || []);
      const studs = Array.isArray(studentsRes.students) ? studentsRes.students : (studentsRes.data || studentsRes || []);
      const structs = feeStructuresRes.data || feeStructuresRes.structures || [];
      const cls = Array.isArray(classesRes) ? classesRes : (classesRes.classes || classesRes.data || []);

      setPayments(pay || []);
      setStudents(studs || []);
      setFeeStructures(structs || []);
      setClasses(cls || []);

      // cache
      try {
        localStorage.setItem(cacheKeyPayments, JSON.stringify(pay || []));
        localStorage.setItem(cacheKeyStudents, JSON.stringify(studs || []));
        localStorage.setItem(cacheKeyStructures, JSON.stringify(structs || []));
        localStorage.setItem(cacheKeyClasses, JSON.stringify(cls || []));
      } catch (_) {}
    } catch (error) {
      setSnackbar({ open: true, message: t('paymentProcessingPage.errors.fetchData'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const normalizeGateway = (g) => {
    const s = String(g || '').toLowerCase();
    if (s === 'mtn_momo' || s === 'mtn') return 'mtn';
    if (s === 'orange_money' || s === 'orange') return 'orange';
    if (s === 'stripe') return 'stripe';
    if (s === 'offline' || s === 'cash' || s === 'bank') return 'offline';
    return s;
  };
  const selectedStructure = feeStructures.find(fs => String(fs.id) === String(paymentForm.feeStructureId));
  const allowedMethods = (() => {
    const raw = Array.isArray(selectedStructure?.paymentGateways) ? selectedStructure.paymentGateways : [];
    const list = raw.map(normalizeGateway).filter(Boolean);
    return list.length ? Array.from(new Set([...list, 'offline'])) : ['mtn','orange','stripe','offline'];
  })();

  const visibleMethods = (() => {
    const out = [];
    for (const m of allowedMethods) {
      if (m === 'offline') { out.push('offline'); continue; }
      if (m === 'mtn' && methodAvailability?.mtn) { out.push('mtn'); continue; }
      if (m === 'stripe' && methodAvailability?.stripe) { out.push('stripe'); continue; }
      if (m === 'orange' && methodAvailability?.orange) { out.push('orange'); continue; }
    }
    return out.length ? out : ['offline'];
  })();

  // Cash needs no gateway and is always offered, so anything beyond it means
  // the school has configured one. Both the method picker and the reference
  // field hang off this: without a gateway there is nothing to choose between
  // and no transfer to reconcile against.
  const hasGateway = visibleMethods.length > 1;

  useEffect(() => {
    if (!visibleMethods.includes(paymentForm.paymentMethod)) {
      setPaymentForm(prev => ({ ...prev, paymentMethod: visibleMethods[0] || 'offline' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentForm.feeStructureId, feeStructures.length, methodAvailability?.mtn, methodAvailability?.stripe, methodAvailability?.orange]);

  // A receipt states a BALANCE, so it has to be built from every completed
  // payment against that fee — not from the one row that was clicked. The old
  // dialog compared a single payment to the whole fee, so a second payment
  // settling a fee still printed "outstanding" and read as incomplete.
  const openReceipt = async (payment) => {
    setReceiptData(null);
    setReceiptOpen(true);
    let prior = [payment];
    try {
      const res = await fetchJson(`/api/finance/payments?studentId=${encodeURIComponent(payment.studentId || payment.Student?.id || '')}&feeStructureId=${encodeURIComponent(payment.feeStructureId || payment.FeeStructure?.id || '')}&status=completed&pageSize=100`, { method: 'GET' });
      const list = Array.isArray(res) ? res : (res?.data || res?.payments || res?.rows || []);
      if (Array.isArray(list) && list.length) prior = list;
    } catch (e) {
      // Fall back to the single row. The receipt then states this payment
      // alone, which is the old behaviour — never a wrong larger claim.
    }
    setReceiptData(buildReceipt({
      payment,
      structure: feeStructures.find((f) => String(f.id) === String(payment.feeStructureId))
        || payment.FeeStructure || null,
      priorPayments: prior,
    }));
  };

  // What the chosen student still owes on the chosen fee, so recording a PART
  // payment is a normal act rather than a guess.
  const loadOutstandingForForm = async (studentId, feeStructureId) => {
    if (!studentId || !feeStructureId) { setFormOutstanding(null); return; }
    try {
      const res = await fetchJson(`/api/finance/payments?studentId=${encodeURIComponent(studentId)}&feeStructureId=${encodeURIComponent(feeStructureId)}&status=completed&pageSize=100`, { method: 'GET' });
      const list = Array.isArray(res) ? res : (res?.data || res?.payments || res?.rows || []);
      const structure = feeStructures.find((f) => String(f.id) === String(feeStructureId));
      const paid = (Array.isArray(list) ? list : []).reduce((n, x) => n + Number(x.amount || 0), 0);
      const { unpaid } = feeStatusFor({ totalPaid: paid, structure: structure || {} });
      setFormOutstanding(unpaid);
    } catch (e) { setFormOutstanding(null); }
  };

  const receiptDocument = () => receiptHtml({
    receipt: receiptData,
    schoolName: schoolName || '',
    labels: {
      title: t('paymentProcessingPage.dialogs.receipt.heading'),
      receiptNo: t('paymentProcessingPage.dialogs.receipt.receiptNo'),
      date: t('paymentProcessingPage.dialogs.receipt.date'),
      student: t('paymentProcessingPage.dialogs.receipt.studentInfo'),
      studentNumber: t('paymentProcessingPage.dialogs.receipt.studentNo'),
      class: t('paymentProcessingPage.dialogs.receipt.class'),
      fee: t('paymentProcessingPage.dialogs.receipt.description'),
      feeTotal: t('paymentProcessingPage.dialogs.receipt.feeTotal'),
      paidToDate: t('paymentProcessingPage.dialogs.receipt.paidToDate', { defaultValue: 'Paid to date' }),
      balance: t('paymentProcessingPage.dialogs.receipt.outstandingBalance'),
      payer: t('paymentProcessingPage.dialogs.receipt.payer', { defaultValue: 'Paid by' }),
      notes: t('paymentProcessingPage.dialogs.receipt.notes'),
      amount: t('paymentProcessingPage.dialogs.receipt.totalPaid'),
      fullyPaid: t('paymentProcessingPage.dialogs.receipt.fullyPaid', { defaultValue: 'FULLY PAID' }),
      partiallyPaid: t('paymentProcessingPage.dialogs.receipt.partiallyPaid', { defaultValue: 'PARTIALLY PAID' }),
      stillOwing: t('paymentProcessingPage.dialogs.receipt.stillOwing', { defaultValue: 'Still outstanding' }),
      footer: t('paymentProcessingPage.dialogs.receipt.footnote'),
    },
  });

  const requestDeletePayment = (payment) => {
    setDeleteTarget(payment || null);
    setDeleteConfirmOpen(true);
  };

  const confirmDeletePayment = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteBusy(true);
      await fetchJson(`/api/finance/payments/${encodeURIComponent(deleteTarget.id)}`, { method: 'DELETE' });
      setPayments(prev => (Array.isArray(prev) ? prev.filter(p => String(p.id) !== String(deleteTarget.id)) : prev));
      setSnackbar({ open: true, message: t('paymentProcessingPage.messages.paymentDeleted'), severity: 'success' });
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      setSnackbar({ open: true, message: e?.message || t('paymentProcessingPage.errors.deletePayment'), severity: 'error' });
    } finally {
      setDeleteBusy(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    // Show confirmation summary before processing
    const student = students.find(s => String(s.id) === String(paymentForm.studentId));
    const feeStructure = feeStructures.find(fs => String(fs.id) === String(paymentForm.feeStructureId));
    const classData = classes.find(c => String(c.id) === String(paymentForm.classId));
    const method = paymentForm.paymentMethod;
    const account =
      method === 'mtn'
        ? (schoolAccounts.mtnAccountNumber || '')
        : method === 'orange'
          ? (schoolAccounts.orangeAccountNumber || '')
          : '';
    setPaymentSummary({
      student: student ? `${student.firstName} ${student.lastName}` : '-',
      class: classData?.name || '-',
      feeStructure: feeStructure?.name || '-',
      amount: paymentForm.amount,
      method,
      account
    });
    setConfirmOpen(true);
  };

  const processPayment = async () => {
    setConfirmOpen(false);
    try {
      const provider = paymentForm.paymentMethod; // 'mtn' | 'orange' | 'stripe'
      if (provider === 'offline') {
        const created = await fetchJson('/api/finance/payments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentId: paymentForm.studentId,
            classId: paymentForm.classId || undefined,
            feeStructureId: paymentForm.feeStructureId,
            amount: paymentForm.amount,
            currency: 'XAF',
            gateway: 'offline',
            method: 'offline',
            transactionId: paymentForm.reference || undefined,
            notes: paymentForm.notes || undefined
          })
        });
        setSnackbar({ open: true, message: t('paymentProcessingPage.messages.manualRecorded'), severity: 'success' });
        if (created?.data) await openReceipt(created.data);
      } else {
        const payload = {
          studentId: paymentForm.studentId,
          classId: paymentForm.classId || undefined,
          feeStructureId: paymentForm.feeStructureId,
          amount: paymentForm.amount,
          gateway: provider,
          msisdn: provider !== 'stripe' ? (paymentForm.msisdn || undefined) : undefined,
          email: provider === 'stripe' ? (paymentForm.payerEmail || undefined) : undefined,
          otp: provider === 'orange' ? (paymentForm.otp || undefined) : undefined,
          notes: paymentForm.notes || undefined
        };
        const resp = await fetchJson('/api/finance/payments/initiate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        setSnackbar({ open: true, message: t('paymentProcessingPage.messages.paymentInitiated'), severity: 'success' });
        if (resp?.data?.redirectUrl || resp?.data?.paymentUrl) {
          const url = resp.data.redirectUrl || resp.data.paymentUrl;
          window.location.href = url;
        } else if (resp?.data?.reference) {
          setSnackbar({ open: true, message: t('paymentProcessingPage.messages.reference', { ref: resp.data.reference }), severity: 'info' });
        }
      }
      resetForm();
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || t('paymentProcessingPage.errors.processPayment'), severity: 'error' });
    }
  };

  const resetForm = () => {
    setPaymentForm({
      classId: '',
      studentId: '',
      feeStructureId: '',
      amount: '',
      paymentMethod: 'mtn',
      msisdn: '',
      payerEmail: '',
      otp: '',
      reference: '',
      notes: ''
    });
    setShowPaymentForm(false);
  };

  const handlePaymentStatusUpdate = async (id, status) => {
    try {
      await fetchJson(`/api/finance/payments/${id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      setSnackbar({ open: true, message: t('paymentProcessingPage.messages.paymentStatusUpdated'), severity: 'success' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: t('paymentProcessingPage.errors.updatePaymentStatus'), severity: 'error' });
    }
  };

  const refreshPaymentStatus = async (id) => {
    try {
      const resp = await fetchJson(`/api/finance/payments/${id}/refresh-status`, { method: 'GET' });
      const next = resp?.data?.status;
      setSnackbar({
        open: true,
        message: next
          ? t('paymentProcessingPage.messages.statusWithValue', { status: next })
          : t('paymentProcessingPage.messages.statusRefreshed'),
        severity: 'success'
      });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: t('paymentProcessingPage.errors.refreshStatus'), severity: 'error' });
    }
  };

  // Currency formatter for XAF (Cameroon)
  const formatXAF = (amount) => new Intl.NumberFormat(i18n.language || 'en-CM', {
    style: 'currency',
    currency: 'XAF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(amount || 0));

  // Note: legacy badge helpers removed in favor of MUI Chips

  const calculateTotal = (payments) => {
    return payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  };

  const providerLabel = (p) => {
    const v = String(p || '').toLowerCase();
    if (v === 'mtn_momo' || v === 'mtn') return t('paymentProcessingPage.providers.mtn');
    if (v === 'orange_money' || v === 'orange') return t('paymentProcessingPage.providers.orange');
    if (v === 'stripe') return t('paymentProcessingPage.providers.stripe');
    if (v === 'offline' || v === 'cash' || v === 'bank') return t('paymentProcessingPage.providers.offline');
    return p;
  };
  const methodLabel = (m) => providerLabel(m);

  // Check if payment form is valid
  const isFormValid = () => {
    // Basic required fields
    if (!paymentForm.classId || !paymentForm.studentId || !paymentForm.feeStructureId || !paymentForm.amount) {
      return false;
    }

    const amountNum = Number(paymentForm.amount);
    if (!(amountNum > 0)) return false;

    // Manual/Offline validation: don't allow over-paying beyond the fee structure amount
    if (paymentForm.paymentMethod === 'offline' && selectedStructure) {
      const max = Number(selectedStructure?.amount || 0);
      if (Number.isFinite(max) && max > 0 && amountNum > max) return false;
    }

    if (paymentForm.paymentMethod === 'mtn') {
      return !!String(paymentForm.msisdn || '').trim();
    }
    if (paymentForm.paymentMethod === 'stripe') {
      return !!String(paymentForm.payerEmail || '').trim();
    }
    if (paymentForm.paymentMethod === 'offline') {
      return true;
    }

    return false;
  };

  const loadOutstanding = async (cid) => {
    if (!cid) {
      setOutstanding([]);
      return;
    }
    try {
      const res = await fetchJson(`/api/finance/payments/outstanding?classId=${cid}`, { method: 'GET' });
      setOutstanding(res.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: t('paymentProcessingPage.errors.loadOutstanding'), severity: 'error' });
    }
  };

  if (loading && (payments || []).length === 0) {
    return <AppLoader variant="circular3" size={48} centered messages={[t('paymentProcessingPage.loading.payments')]} />;
  }

  return (
    <div className="p-4 sm:p-6">
      {/* The <h1> that stood here read "Payment Processing" — the name of a
          page that was merged away, printed larger than the tab that actually
          switches to it. The tab is the heading now; the action stays. */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-2">
        <Typography variant="body2" color="text.secondary">
          {t('paymentProcessingPage.subheader', { defaultValue: 'Record money received, approve what parents have sent, and issue receipts.' })}
        </Typography>
        <Button onClick={() => setShowPaymentForm(true)}>{t('paymentProcessingPage.buttons.openForm')}</Button>
        <Dialog open={showPaymentForm} onClose={() => setShowPaymentForm(false)} maxWidth="lg" fullWidth>
          {/* With a default behind it. i18next renders the KEY when a
              translation is missing, and this one was missing entirely — the
              dialog was titled "paymentProcessingPage.dialogs.newPayment.title"
              on screen. A default turns that failure back into English. */}
          <DialogTitle>{t('paymentProcessingPage.dialogs.newPayment.title', 'Record a payment')}</DialogTitle>
          <DialogContent sx={{ paddingTop: 3 }}>
            <form id="paymentForm" onSubmit={handlePaymentSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormControl fullWidth size="medium">
                  <InputLabel id="class-label">{t('paymentProcessingPage.form.class.label')}</InputLabel>
                  <Select
                    labelId="class-label"
                    label={t('paymentProcessingPage.form.class.label')}
                    value={paymentForm.classId}
                    onChange={(e) => {
                      setPaymentForm({ ...paymentForm, classId: e.target.value, studentId: '', feeStructureId: '' });
                    }}
                    required
                  >
                    <MenuItem value="">{t('paymentProcessingPage.form.class.placeholder')}</MenuItem>
                    {classes.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="medium">
                  <InputLabel id="student-label">{t('paymentProcessingPage.form.student.label')}</InputLabel>
                  <Select
                    labelId="student-label"
                    label={t('paymentProcessingPage.form.student.label')}
                    value={paymentForm.studentId}
                    onChange={(e) => {
                      setPaymentForm({ ...paymentForm, studentId: e.target.value });
                      setFormOutstanding(null);
                      loadOutstandingForForm(e.target.value, paymentForm.feeStructureId);
                    }}
                    required
                    disabled={!paymentForm.classId}
                  >
                    <MenuItem value="">{t('paymentProcessingPage.form.student.placeholder')}</MenuItem>
                    {students
                      .filter(s => paymentForm.classId && String(s.classId) === String(paymentForm.classId))
                      .map(student => (
                        <MenuItem key={student.id} value={student.id}>
                          {student.firstName} {student.lastName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="medium">
                  <InputLabel id="fee-structure-label">{t('paymentProcessingPage.form.feeStructure.label')}</InputLabel>
                  <Select
                    labelId="fee-structure-label"
                    label={t('paymentProcessingPage.form.feeStructure.label')}
                    value={paymentForm.feeStructureId}
                    onChange={(e) => {
                      setPaymentForm({ ...paymentForm, feeStructureId: e.target.value, amount: '' });
                      setFormOutstanding(null);
                      loadOutstandingForForm(paymentForm.studentId, e.target.value);
                    }}
                    required
                    disabled={!paymentForm.classId}
                  >
                    <MenuItem value="">{t('paymentProcessingPage.form.feeStructure.placeholder')}</MenuItem>
                    {feeStructures
                      .filter(structure => paymentForm.classId && String(structure.classId) === String(paymentForm.classId))
                      .map(structure => (
                        <MenuItem key={structure.id} value={structure.id}>
                          {structure.name} - {formatXAF(structure.amount)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  label={t('paymentProcessingPage.form.amount.label')}
                  type="number"
                  size="medium"
                  fullWidth
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                  helperText={formOutstanding == null
                    ? t('paymentProcessingPage.form.amount.helperAuto')
                    : Number(paymentForm.amount) > formOutstanding
                      // Taking more than the fee asks is not blocked — the money
                      // is in the drawer either way. It is named instead, so the
                      // person at the counter knows a refund follows.
                      ? t('paymentProcessingPage.form.amount.helperOverpay', {
                        amount: formatXAF(Number(paymentForm.amount) - formOutstanding),
                        defaultValue: '{{amount}} more than this fee requires. The parent will be asked to collect the refund.',
                      })
                      : t('paymentProcessingPage.form.amount.helperOutstanding', {
                        amount: formatXAF(formOutstanding),
                        defaultValue: 'Outstanding: {{amount}}. Enter less to record a part payment.',
                      })}
                  required
                />

                {/* A PART PAYMENT is the normal case, not an exception: a parent
                    pays what they have. So the amount starts empty, what is
                    still owed is stated, and paying it in full is one click —
                    the receipt then says "partially paid" on its own. */}
                {formOutstanding > 0 && (
                  <Button
                    size="small"
                    onClick={() => setPaymentForm({ ...paymentForm, amount: String(formOutstanding) })}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {t('paymentProcessingPage.form.amount.payAll', {
                      amount: formatXAF(formOutstanding),
                      defaultValue: 'Pay all {{amount}}',
                    })}
                  </Button>
                )}

                {/* Asked only when there IS a choice. A school with no gateway
                    configured takes cash, and a one-item picker is a question
                    with a single answer. */}
                {hasGateway && (
                  <FormControl fullWidth size="medium">
                    <InputLabel id="method-label">{t('paymentProcessingPage.form.method.label')}</InputLabel>
                    <Select
                      labelId="method-label"
                      label={t('paymentProcessingPage.form.method.label')}
                      value={paymentForm.paymentMethod}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                    >
                      {visibleMethods.includes('mtn') && (
                        <MenuItem value="mtn">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <img src="/Mtn-momo.png" alt={t('paymentProcessingPage.providers.mtn')} style={{ height: 20 }} />
                            <span>{t('paymentProcessingPage.providers.mtn')}</span>
                          </Box>
                        </MenuItem>
                      )}
                      {visibleMethods.includes('orange') && (
                        <MenuItem value="orange">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <img src="/orange-money.png" alt={t('paymentProcessingPage.providers.orange')} style={{ height: 20 }} />
                            <span>{t('paymentProcessingPage.providers.orange')}</span>
                          </Box>
                        </MenuItem>
                      )}
                      {visibleMethods.includes('stripe') && (
                        <MenuItem value="stripe">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CreditCardIcon sx={{ fontSize: 20 }} />
                            <span>{t('paymentProcessingPage.providers.stripe')}</span>
                          </Box>
                        </MenuItem>
                      )}
                      {visibleMethods.includes('offline') && (
                        <MenuItem value="offline">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{t('paymentProcessingPage.providers.cash', { defaultValue: 'Cash' })}</span>
                          </Box>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}

                {paymentForm.paymentMethod === 'mtn' && (
                  <TextField
                    label={t('paymentProcessingPage.form.mtn.phone.label')}
                    size="medium"
                    fullWidth
                    value={paymentForm.msisdn}
                    onChange={(e) => setPaymentForm({ ...paymentForm, msisdn: e.target.value })}
                    placeholder={t('paymentProcessingPage.form.mtn.phone.placeholder')}
                    helperText={t('paymentProcessingPage.form.mtn.phone.helper')}
                    required
                  />
                )}

                {/* MTN MoMo - Only customer-facing fields */}

                {paymentForm.paymentMethod === 'stripe' && (
                  <TextField
                    label={t('paymentProcessingPage.form.stripe.email.label')}
                    type="email"
                    size="medium"
                    fullWidth
                    value={paymentForm.payerEmail}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payerEmail: e.target.value })}
                    placeholder={t('paymentProcessingPage.form.stripe.email.placeholder')}
                    helperText={t('paymentProcessingPage.form.stripe.email.helper')}
                    required
                  />
                )}

                {/* A reference only exists where a gateway does: a MoMo or bank
                    transfer carries one, plain cash does not. So it is asked
                    for an offline payment ONLY at a school that has a gateway
                    configured. It still never appears on the receipt. */}
                {hasGateway && paymentForm.paymentMethod === 'offline' && (
                  <TextField
                    label={t('paymentProcessingPage.form.offline.reference.label')}
                    size="medium"
                    fullWidth
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                    placeholder={t('paymentProcessingPage.form.offline.reference.placeholder')}
                  />
                )}

                {paymentForm.paymentMethod === 'mtn' && schoolAccounts.mtnAccountNumber && (
                  <div className="md:col-span-2">
                    <Alert severity="info">
                      {t('paymentProcessingPage.form.mtn.accountInfo', { account: schoolAccounts.mtnAccountNumber })}
                    </Alert>
                  </div>
                )}

                {/* Date and reference handled automatically by backend */}
              </div>

              <TextField
                label={t('paymentProcessingPage.form.notes.label')}
                size="medium"
                fullWidth
                multiline
                rows={2}
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder={t('paymentProcessingPage.form.notes.placeholder')}
              />
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetForm} color="inherit">{t('actions.cancel')}</Button>
            <Button 
              type="submit" 
              form="paymentForm" 
              variant="contained"
              disabled={!isFormValid()}
            >
              {paymentForm.paymentMethod === 'stripe'
                ? t('paymentProcessingPage.buttons.proceedCard')
                : t('paymentProcessingPage.buttons.processPayment')}
            </Button>
          </DialogActions>
        </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => (!deleteBusy ? setDeleteConfirmOpen(false) : null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('paymentProcessingPage.dialogs.deletePayment.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t('paymentProcessingPage.dialogs.deletePayment.content')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteBusy} color="inherit">{t('actions.cancel')}</Button>
          <Button onClick={confirmDeletePayment} disabled={deleteBusy} variant="contained" color="error">{t('actions.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('paymentProcessingPage.dialogs.details.title')}</DialogTitle>
        <DialogContent>
          <div className="space-y-2">
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.student')}:</strong> {detailsPayment?.Student ? `${detailsPayment.Student.firstName} ${detailsPayment.Student.lastName}` : '-'}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.class')}:</strong> {detailsPayment?.Class?.name || '-'}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.feeStructure')}:</strong> {detailsPayment?.FeeStructure?.name || '-'}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.amount')}:</strong> {formatXAF(detailsPayment?.amount || 0)}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.provider')}:</strong> {providerLabel(detailsPayment?.provider)}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.status')}:</strong> {detailsPayment?.status || '-'}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.reference')}:</strong> {detailsPayment?.providerReference || '-'}</div>
            <div><strong>{t('paymentProcessingPage.dialogs.details.labels.date')}:</strong> {detailsPayment?.createdAt ? new Date(detailsPayment.createdAt).toLocaleString(i18n.language || undefined) : '-'}</div>
            {detailsPayment?.notes && (
              <div><strong>{t('paymentProcessingPage.dialogs.details.labels.notes')}:</strong> {detailsPayment.notes}</div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} color="inherit">{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label={t('paymentProcessingPage.tabs.completed')} value="completed" />
          <Tab label={t('paymentProcessingPage.tabs.pending')} value="pending" />
          <Tab label={t('paymentProcessingPage.tabs.failed')} value="failed" />
        </Tabs>
      </Box>

      {tab === 'completed' && (
          <Card>
            <CardHeader
              title={t('paymentProcessingPage.tabs.completed')}
              subheader={t('paymentProcessingPage.cards.total', { total: formatXAF(calculateTotal(payments.filter(p => p.status === 'completed'))) })}
            />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.student')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.feeStructure')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.amount')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.method')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.status')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.date')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.reference')}</TableCell>
                    <TableCell component="th">{t('actions.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.filter(p => p.status === 'completed').map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.Student ? `${payment.Student.firstName} ${payment.Student.lastName}` : '-'}
                      </TableCell>
                      <TableCell>{payment.FeeStructure?.name || '-'}</TableCell>
                      <TableCell>{formatXAF(payment.amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={providerLabel(payment.provider || payment.method || 'n/a')} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`paymentProcessingPage.statuses.${payment.status}`, { defaultValue: payment.status })}
                          color={payment.status === 'completed' ? 'success' : payment.status === 'pending' ? 'warning' : payment.status === 'failed' ? 'error' : 'info'}
                        />
                      </TableCell>
                      <TableCell>
                        {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString(i18n.language || undefined) : '-'}
                      </TableCell>
                      <TableCell>{payment.providerReference || '-'}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {payment.status === 'pending' && (
                            <>
                              {['mtn_momo', 'orange_money'].includes(String(payment.provider || '').toLowerCase()) && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => refreshPaymentStatus(payment.id)}
                                >
                                  {t('paymentProcessingPage.buttons.refresh')}
                                </Button>
                              )}
                              <Button
                                size="small"
                                onClick={() => handlePaymentStatusUpdate(payment.id, 'completed')}
                              >
                                {t('paymentProcessingPage.buttons.complete')}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handlePaymentStatusUpdate(payment.id, 'failed')}
                              >
                                {t('paymentProcessingPage.buttons.fail')}
                              </Button>
                            </>
                          )}
                          {canIssueReceipt(payment) && (
                            <Button size="small" variant="outlined" onClick={() => openReceipt(payment)}>
                              {t('paymentProcessingPage.buttons.view')}
                            </Button>
                          )}
                          <Button size="small" onClick={() => { setDetailsPayment(payment); setDetailsOpen(true); }}>
                            {t('paymentProcessingPage.buttons.details')}
                          </Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => requestDeletePayment(payment)}>
                            {t('actions.delete')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
      )}

      {tab === 'failed' && (
          <Card>
            <CardHeader
              title={t('paymentProcessingPage.tabs.failed')}
              subheader={t('paymentProcessingPage.cards.total', { total: formatXAF(calculateTotal(payments.filter(p => p.status === 'failed'))) })}
            />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.student')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.feeStructure')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.amount')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.method')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.date')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.failed.headers.reason')}</TableCell>
                    <TableCell component="th">{t('actions.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.filter(p=>p.status==='failed').map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.Student ? `${payment.Student.firstName} ${payment.Student.lastName}` : '-'}
                      </TableCell>
                      <TableCell>{payment.FeeStructure?.name || '-'}</TableCell>
                      <TableCell>{formatXAF(payment.amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={providerLabel(payment.provider || payment.method || 'n/a')} />
                      </TableCell>
                      <TableCell>
                        {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString(i18n.language || undefined) : '-'}
                      </TableCell>
                      <TableCell>{payment.failureReason || '-'}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {canIssueReceipt(payment) && (
                            <Button size="small" variant="outlined" onClick={() => openReceipt(payment)}>
                              {t('paymentProcessingPage.buttons.view')}
                            </Button>
                          )}
                          <Button size="small" onClick={() => { setDetailsPayment(payment); setDetailsOpen(true); }}>
                            {t('paymentProcessingPage.buttons.details')}
                          </Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => requestDeletePayment(payment)}>
                            {t('actions.delete')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
      )}

      {tab === 'pending' && (
          <Card>
            <CardHeader
              title={t('paymentProcessingPage.tabs.pending')}
              subheader={t('paymentProcessingPage.cards.total', { total: formatXAF(calculateTotal(payments.filter(p => p.status === 'pending'))) })}
            />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.student')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.feeStructure')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.amount')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.method')}</TableCell>
                    <TableCell component="th">{t('paymentProcessingPage.tables.completed.headers.date')}</TableCell>
                    <TableCell component="th">{t('actions.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.filter(p=>p.status==='pending').map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.Student ? `${payment.Student.firstName} ${payment.Student.lastName}` : '-'}
                      </TableCell>
                      <TableCell>{payment.FeeStructure?.name || '-'}</TableCell>
                      <TableCell>{formatXAF(payment.amount)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={providerLabel(payment.provider || payment.method || 'n/a')} />
                      </TableCell>
                      <TableCell>
                        {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString(i18n.language || undefined) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!['mtn_momo', 'orange_money'].includes(String(payment.provider || '').toLowerCase())}
                            onClick={() => refreshPaymentStatus(payment.id)}
                          >
                            {t('paymentProcessingPage.buttons.refresh')}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => handlePaymentStatusUpdate(payment.id, 'completed')}
                          >
                            {t('paymentProcessingPage.buttons.complete')}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handlePaymentStatusUpdate(payment.id, 'failed')}
                          >
                            {t('paymentProcessingPage.buttons.fail')}
                          </Button>
                          {canIssueReceipt(payment) && (
                            <Button size="small" variant="outlined" onClick={() => openReceipt(payment)}>
                              {t('paymentProcessingPage.buttons.view')}
                            </Button>
                          )}
                          <Button size="small" onClick={() => { setDetailsPayment(payment); setDetailsOpen(true); }}>
                            {t('paymentProcessingPage.buttons.details')}
                          </Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => requestDeletePayment(payment)}>
                            {t('actions.delete')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('paymentProcessingPage.dialogs.confirm.title')}</DialogTitle>
        <DialogContent>
          <div className="space-y-3 pt-2">
            <div className="border-b pb-2">
              <div className="text-sm text-gray-500">{t('paymentProcessingPage.dialogs.confirm.labels.student')}</div>
              <div className="font-semibold">{paymentSummary?.student}</div>
            </div>
            <div className="border-b pb-2">
              <div className="text-sm text-gray-500">{t('paymentProcessingPage.dialogs.confirm.labels.class')}</div>
              <div className="font-semibold">{paymentSummary?.class}</div>
            </div>
            <div className="border-b pb-2">
              <div className="text-sm text-gray-500">{t('paymentProcessingPage.dialogs.confirm.labels.feeStructure')}</div>
              <div className="font-semibold">{paymentSummary?.feeStructure}</div>
            </div>
            <div className="border-b pb-2">
              <div className="text-sm text-gray-500">{t('paymentProcessingPage.dialogs.confirm.labels.amount')}</div>
              <div className="font-semibold text-lg text-green-600">{formatXAF(paymentSummary?.amount || 0)}</div>
            </div>
            <div className="border-b pb-2">
              <div className="text-sm text-gray-500">{t('paymentProcessingPage.dialogs.confirm.labels.method')}</div>
              <div className="font-semibold">{providerLabel(paymentSummary?.method)}</div>
            </div>
            {paymentSummary?.account && (
              <div className="border-b pb-2">
                <div className="text-sm text-gray-500">{t('paymentProcessingPage.dialogs.confirm.labels.account')}</div>
                <div className="font-semibold">{paymentSummary.account}</div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">{t('actions.cancel')}</Button>
          <Button onClick={processPayment} variant="contained" color="primary">{t('paymentProcessingPage.dialogs.confirm.confirmAndPay')}</Button>
        </DialogActions>
      </Dialog>

      {/* THE RECEIPT — same shape as mobile's, same figures, same printed
          document. Every number comes from buildReceipt, so nothing on this
          dialog can disagree with what prints or with the Who-owes tab.

          Gone from it: payment METHOD and REFERENCE. For a school taking cash
          they say nothing a parent needs, and a receipt's job is to state what
          was paid and what remains. */}
      <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('paymentProcessingPage.dialogs.receipt.title')}</DialogTitle>
        <DialogContent>
          {!receiptData ? (
            <div className="py-8"><AppLoader /></div>
          ) : (
            <div id="receipt-content" className="p-6 bg-white" style={{ fontFamily: 'Arial, sans-serif' }}>
              <div className="mb-4">
                <div className="text-lg font-bold text-gray-800">{schoolName || t('paymentProcessingPage.dialogs.receipt.schoolFallback')}</div>
                <div className="text-xs text-gray-500">{t('paymentProcessingPage.dialogs.receipt.heading')}</div>
              </div>

              <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    [t('paymentProcessingPage.dialogs.receipt.receiptNo'), receiptData.receiptNo],
                    [t('paymentProcessingPage.dialogs.receipt.date'), receiptData.date],
                    [t('paymentProcessingPage.dialogs.receipt.studentInfo'), receiptData.studentName],
                    [t('paymentProcessingPage.dialogs.receipt.studentNo'), receiptData.studentNumber],
                    [t('paymentProcessingPage.dialogs.receipt.class'), receiptData.className],
                    [t('paymentProcessingPage.dialogs.receipt.description'), receiptData.feeName],
                  ].filter(([, v]) => v || v === 0).map(([k, v]) => (
                    <tr key={k}>
                      <td className="py-1 text-gray-500" style={{ width: '46%', borderBottom: '1px solid #eee' }}>{k}</td>
                      <td className="py-1" style={{ borderBottom: '1px solid #eee' }}>{v}</td>
                    </tr>
                  ))}
                  {receiptData.feeTotal > 0 && (
                    <tr>
                      <td className="py-1 text-gray-500" style={{ borderBottom: '1px solid #eee' }}>{t('paymentProcessingPage.dialogs.receipt.feeTotal')}</td>
                      <td className="py-1" style={{ borderBottom: '1px solid #eee' }}>{formatXAF(receiptData.feeTotal)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="py-1 text-gray-500" style={{ borderBottom: '1px solid #eee' }}>{t('paymentProcessingPage.dialogs.receipt.paidToDate', { defaultValue: 'Paid to date' })}</td>
                    <td className="py-1" style={{ borderBottom: '1px solid #eee' }}>{formatXAF(receiptData.paidToDate)}</td>
                  </tr>
                  {receiptData.feeTotal > 0 && (
                    <tr>
                      <td className="py-1 text-gray-500" style={{ borderBottom: '1px solid #eee' }}>{t('paymentProcessingPage.dialogs.receipt.outstandingBalance')}</td>
                      <td className="py-1" style={{ borderBottom: '1px solid #eee' }}>{formatXAF(receiptData.balance)}</td>
                    </tr>
                  )}
                  {receiptData.payerName && (
                    <tr>
                      <td className="py-1 text-gray-500" style={{ borderBottom: '1px solid #eee' }}>{t('paymentProcessingPage.dialogs.receipt.payer', { defaultValue: 'Paid by' })}</td>
                      <td className="py-1" style={{ borderBottom: '1px solid #eee' }}>{receiptData.payerName}</td>
                    </tr>
                  )}
                  {receiptData.notes && (
                    <tr>
                      <td className="py-1 text-gray-500" style={{ borderBottom: '1px solid #eee' }}>{t('paymentProcessingPage.dialogs.receipt.notes')}</td>
                      <td className="py-1" style={{ borderBottom: '1px solid #eee' }}>{receiptData.notes}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="mt-4 text-2xl font-bold">{formatXAF(receiptData.amountPaid)}</div>
              <div className="text-xs text-gray-500">{t('paymentProcessingPage.dialogs.receipt.totalPaid')}</div>

              <div className="mt-3">
                <span
                  className="inline-block px-3 py-1 rounded text-xs font-bold"
                  style={receiptData.status === 'partially_paid'
                    ? { color: '#8A5A00', background: '#FDF1D8' }
                    : { color: '#12603C', background: '#E4F5EC' }}
                >
                  {receiptData.status === 'partially_paid'
                    ? t('paymentProcessingPage.dialogs.receipt.partiallyPaid', { defaultValue: 'PARTIALLY PAID' })
                    : t('paymentProcessingPage.dialogs.receipt.fullyPaid', { defaultValue: 'FULLY PAID' })}
                </span>
              </div>

              {receiptData.status === 'partially_paid' && (
                <div className="mt-2 text-sm" style={{ color: '#8A5A00' }}>
                  {t('paymentProcessingPage.dialogs.receipt.stillOwing', { defaultValue: 'Still outstanding' })}: {formatXAF(receiptData.balance)}
                </div>
              )}

              <div className="mt-6 pt-3 text-xs text-gray-500" style={{ borderTop: '1px solid #d1d5db' }}>
                {t('paymentProcessingPage.dialogs.receipt.footnote')}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptOpen(false)} color="inherit">{t('common.close')}</Button>

          {/* Print and Download now write THE SAME DOCUMENT — receiptHtml.
              Download used to write the dialog's innerHTML with no stylesheet,
              so the saved file looked nothing like the printed one. */}
          <Button
            disabled={!receiptData}
            onClick={() => {
              const w = window.open('', '_blank');
              if (!w) return;
              w.document.write(receiptDocument());
              w.document.close();
              w.focus();
              setTimeout(() => w.print(), 250);
            }}
            variant="outlined"
          >
            {t('paymentProcessingPage.buttons.print')}
          </Button>

          <Button
            disabled={!receiptData}
            onClick={() => {
              const id = receiptData?.receiptNo || t('paymentProcessingPage.dialogs.receipt.fallbackId');
              const blob = new Blob([receiptDocument()], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = t('paymentProcessingPage.dialogs.receipt.downloadFilename', { id });
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            }}
            variant="contained"
          >
            {t('paymentProcessingPage.buttons.download')}
          </Button>
        </DialogActions>
      </Dialog>

      {false && tab === 'outstanding' && (
        <Card>
          <CardHeader title={t('paymentProcessingPage.outstanding.title')}>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <FormControl fullWidth size="small">
                <InputLabel id="class-select-label">{t('paymentProcessingPage.outstanding.classSelect.label')}</InputLabel>
                <Select
                  labelId="class-select-label"
                  label={t('paymentProcessingPage.outstanding.classSelect.label')}
                  value={selectedClassId}
                  onChange={async (e) => { setSelectedClassId(e.target.value); await loadOutstanding(e.target.value); }}
                >
                  {classes.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('paymentProcessingPage.tables.outstanding.headers.student')}</TableCell>
                  <TableCell>{t('paymentProcessingPage.tables.outstanding.headers.fee')}</TableCell>
                  <TableCell>{t('paymentProcessingPage.tables.outstanding.headers.required')}</TableCell>
                  <TableCell>{t('paymentProcessingPage.tables.outstanding.headers.paid')}</TableCell>
                  <TableCell>{t('paymentProcessingPage.tables.outstanding.headers.outstanding')}</TableCell>
                  <TableCell>{t('actions.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outstanding.map(row => (
                  <TableRow key={`${row.studentId}-${row.feeStructureId}`}>
                    <TableCell>{row.studentName}</TableCell>
                    <TableCell>{row.feeName}</TableCell>
                    <TableCell>{formatXAF(row.required)}</TableCell>
                    <TableCell>{formatXAF(row.paid)}</TableCell>
                    <TableCell>
                      <Chip size="small" color={row.outstanding > 0 ? 'warning' : 'success'} label={formatXAF(row.outstanding)} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button size="small" variant="contained" onClick={() => {
                          setPaymentForm(prev => ({
                            ...prev,
                            classId: row.classId,
                            studentId: row.studentId,
                            feeStructureId: row.feeStructureId,
                            amount: row.outstanding,
                            paymentMethod: 'mtn'
                          }));
                          setShowPaymentForm(true);
                        }}>{t('paymentProcessingPage.outstanding.buttons.payMtn')}</Button>
                        <Button size="small" variant="contained" color="warning" onClick={() => {
                          setPaymentForm(prev => ({
                            ...prev,
                            classId: row.classId,
                            studentId: row.studentId,
                            feeStructureId: row.feeStructureId,
                            amount: row.outstanding,
                            paymentMethod: 'orange'
                          }));
                          setShowPaymentForm(true);
                        }}>{t('paymentProcessingPage.outstanding.buttons.payOrange')}</Button>
                        <Button size="small" variant="outlined" onClick={() => {
                          setPaymentForm(prev => ({
                            ...prev,
                            classId: row.classId,
                            studentId: row.studentId,
                            feeStructureId: row.feeStructureId,
                            amount: row.outstanding,
                            paymentMethod: 'stripe'
                          }));
                          setShowPaymentForm(true);
                        }}>{t('paymentProcessingPage.outstanding.buttons.payCard')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PaymentProcessingPage;
